import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useBots, useReconcile } from '../../api/hooks';
import { botNeedsAttention } from '../../domain/invariants';
import { DESIRED_LABEL, EFFECTIVE_LABEL } from '../../domain/labels';
import { api } from '../../api/http';
import { Avatar, Button, DataTable, ErrorNote, IconButton, Icons, Loading, PageHeader, Stat, StatePill } from '../../ui';
import type { Bot } from '../../domain/types';

const VIEW_KEY = 'tbm.bots.view';
type BotsView = 'pads' | 'table';

function readView(): BotsView {
  try {
    return localStorage.getItem(VIEW_KEY) === 'table' ? 'table' : 'pads';
  } catch {
    return 'pads';
  }
}

function AddBotItem({ className }: { className: string }): JSX.Element {
  return (
    <a className={className} href={api.botOauthUrl()} data-testid="bot-add">
      <Icons.plus />
      <strong>Ajouter un bot</strong>
    </a>
  );
}

export function DashboardPage(): JSX.Element {
  const bots = useBots();
  const reconcile = useReconcile();
  const [view, setView] = useState<BotsView>(readView);
  if (bots.isPending) return <Loading />;
  if (bots.isError) return <ErrorNote>{bots.error.message}</ErrorNote>;
  const list = bots.data ?? [];
  const drift = list.filter(botNeedsAttention);
  const grantPending = list.reduce((n, b) => n + b.counts.subscriptionsGrantPending, 0);

  const setPersistedView = (next: BotsView): void => {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Vue d’ensemble"
        title="Tableau de bord"
        actions={
          <>
            <IconButton
              data-testid="view-pads"
              aria-pressed={view === 'pads'}
              aria-label="Vue pads"
              onClick={() => setPersistedView('pads')}
            >
              <Icons.grid />
            </IconButton>
            <IconButton
              data-testid="view-table"
              aria-pressed={view === 'table'}
              aria-label="Vue tableau"
              onClick={() => setPersistedView('table')}
            >
              <Icons.table />
            </IconButton>
            <Button onClick={() => reconcile.mutate({})} loading={reconcile.isPending}>
              Réconcilier
            </Button>
          </>
        }
      />
      {drift.length ? (
        <div className="tbm-banner" data-testid="drift-banner">
          {drift.length} bot(s) en dérive — {grantPending} abonnement(s) en attente de scope.
        </div>
      ) : null}
      <div className="tbm-grid">
        <div className="tbm-span-3"><Stat label="Bots" value={list.length} /></div>
        <div className="tbm-span-3"><Stat label="Dérives" value={drift.length} /></div>
        <div className="tbm-span-3"><Stat label="Scopes manquants" value={grantPending} /></div>
        <div className="tbm-span-3"><Stat label="Échecs EventSub" value={list.reduce((n, b) => n + b.counts.subscriptionsFailed, 0)} /></div>
      </div>
      {view === 'pads' ? (
        <div className="tbm-pad-grid">
          <AddBotItem className="tbm-pad tbm-pad--add" />
          {list.map((bot) => (
            <BotPad key={bot.id} bot={bot} />
          ))}
        </div>
      ) : (
        <DataTable
          rows={list}
          leading={
            <tr>
              <td colSpan={4}>
                <AddBotItem className="tbm-add-row" />
              </td>
            </tr>
          }
          columns={[
            {
              key: 'bot',
              header: 'Bot',
              cell: (bot: Bot) => (
                <Link to={`/bots/${bot.id}`} data-testid={`bot-${bot.id}`} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Avatar src={bot.identity?.avatarUrl} name={bot.name} />
                  <span>
                    {bot.name}
                    <div className="muted">@{bot.identity?.login ?? bot.id}</div>
                  </span>
                </Link>
              ),
            },
            {
              key: 'desired',
              header: 'Intent',
              cell: (bot) => <StatePill state={bot.desiredState}>{DESIRED_LABEL[bot.desiredState]}</StatePill>,
            },
            {
              key: 'effective',
              header: 'Effectif',
              cell: (bot) => (
                <span data-testid={`state-${bot.id}`}>
                  <StatePill state={bot.health.effectiveState}>{EFFECTIVE_LABEL[bot.health.effectiveState]}</StatePill>
                </span>
              ),
            },
            { key: 'channels', header: 'Chaînes', cell: (bot) => bot.counts.channels },
          ]}
        />
      )}
    </>
  );
}

function BotPad({ bot }: { bot: Bot }): JSX.Element {
  const attention = botNeedsAttention(bot);
  return (
    <Link
      to={`/bots/${bot.id}`}
      data-testid={`bot-${bot.id}`}
      className={`tbm-pad${attention ? ' tbm-pad--drift' : ''}`}
    >
      <Avatar src={bot.identity?.avatarUrl} name={bot.name} />
      <strong>{bot.name}</strong>
      <span className="muted">@{bot.identity?.login ?? bot.id}</span>
      <span data-testid={`state-${bot.id}`}>
        <StatePill state={bot.health.effectiveState}>{EFFECTIVE_LABEL[bot.health.effectiveState]}</StatePill>
      </span>
      <span className="tbm-pad__meta">
        <StatePill state={bot.desiredState}>{DESIRED_LABEL[bot.desiredState]}</StatePill>
        {bot.counts.channels} chaîne(s)
      </span>
    </Link>
  );
}
